import { useEffect, useMemo, useState } from 'react';
import { Header } from './Header';
import { Dropzone } from './Dropzone';
import { FileList } from './FileList';
import { PricingCard } from './PricingCard';
import { AckList } from './AckList';
import { useTheme } from '../hooks/useTheme';
import { useUploadSession } from '../hooks/useUploadSession';
import { makeApi } from '../lib/api';

function collectTriggers(files) {
  const trigs = new Set();
  for (const f of files) {
    if (f.rejected) continue;
    for (const w of (f.warnings || [])) {
      switch (w.code) {
        case 'semi_transparent_detected': trigs.add('if_semi_transparent_detected'); break;
        case 'color_space_warning': trigs.add('if_color_space_warning'); break;
        case 'embedded_raster_detected': trigs.add('if_embedded_raster'); break;
        case 'low_dpi': trigs.add('if_low_dpi'); break;
      }
    }
    if (['poor', 'fair'].includes(f.dpiRating)) trigs.add('if_low_dpi');
  }
  return trigs;
}

export function App({ boot }) {
  const api = useMemo(() => makeApi(boot), [boot]);
  const [theme, setTheme] = useTheme(boot.config.theme.default);
  const { token, files, quote, uploading, upload, remove, setQty } = useUploadSession(api);

  const visibleAcks = useMemo(() => {
    const trigs = collectTriggers(files);
    return boot.config.acknowledgements.filter(a => a.trigger === 'always' || trigs.has(a.trigger));
  }, [files, boot.config.acknowledgements]);

  const [acks, setAcks] = useState({});
  const visibleKeys = visibleAcks.map(a => a.key).join(',');
  useEffect(() => {
    setAcks(prev => {
      const next = {};
      for (const a of visibleAcks) if (prev[a.key]) next[a.key] = true;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKeys]);

  const allAck = visibleAcks.every(a => !a.required || !!acks[a.key]);

  const handleAddToCart = async () => {
    const acceptedAcks = Object.keys(acks).filter(k => acks[k]);
    const result = await api.addToCart(token, acceptedAcks);
    if (result.cartUrl) window.location.href = result.cartUrl;
    else if (result.error) alert(result.error);
  };

  return (
    <div className={`limu-uploader layout-sidebar theme-${theme}`}>
      <Header copy={boot.config.copy} theme={theme} onTheme={setTheme} />
      <div className="grid">
        <section className="col-main">
          <Dropzone
            copy={boot.config.copy}
            onSelect={upload}
            accept={boot.config.validation.acceptedExtensions.map(e => '.' + e).join(',')}
            maxBytes={boot.config.validation.maxFileSizeBytes}
          />
          <div className="files-head">
            <h2 className="sec-title">
              {boot.config.copy.files_section_title} <span className="count">{files.length}</span>
            </h2>
          </div>
          <FileList
            files={files}
            uploading={uploading}
            copy={boot.config.copy}
            onRemove={remove}
            onQty={setQty}
          />
        </section>
        <aside className="col-side">
          <PricingCard
            quote={quote}
            copy={boot.config.copy}
            currency={boot.currency}
            allAck={allAck}
            onAddToCart={handleAddToCart}
          />
          <div className="card ack-card">
            <div className="pc-eyebrow">Acknowledgements</div>
            <AckList items={visibleAcks} checked={acks} onToggle={(k, v) => setAcks(s => ({ ...s, [k]: v }))} />
          </div>
        </aside>
      </div>
    </div>
  );
}
